import { createContext, useContext, useReducer, Reducer, useCallback } from 'react';
import { BhImage } from 'models/BhImage';
import { emptyFunction } from 'utils';

export interface AttributeProductImagePayload {
  productImage: BhImage;
}
export interface AttributeHoverPayload {
  hoverImage: BhImage;
  displayName?: string;
  description?: string;
}

interface OwnState {
  productImage?: BhImage;
  hoverImage?: BhImage;
  displayName?: string;
  description?: string;
}

interface ImageAttributeContextState extends OwnState {
  hover: (args: AttributeHoverPayload) => void;
  resetHover: () => void;
  setProductImage: (args: AttributeProductImagePayload) => void;
  resetProductImage: () => void;
}

const ImageAttributeContext = createContext<ImageAttributeContextState>({
  hover: emptyFunction,
  resetHover: emptyFunction,
  setProductImage: emptyFunction,
  resetProductImage: emptyFunction
});

export const useImageAttributeContext = () => {
  const ctx = useContext(ImageAttributeContext);
  if (ctx === undefined) {
    throw new Error('useBigMediaHoverContext must be used with BigMediaHoverProvider');
  }

  return ctx;
};

type Action =
  | ({
      type: 'setHoverData';
    } & AttributeHoverPayload)
  | { type: 'resetHoverData' }
  | ({ type: 'setProductImage' } & AttributeProductImagePayload)
  | { type: 'resetProductImage' };

const ownReducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'setHoverData': {
      const { type: _, ...data } = action;
      return { ...state, ...data };
    }
    case 'setProductImage': {
      const { type: _, ...data } = action;
      return { ...state, ...data };
    }
    case 'resetProductImage': {
      return { ...state, productImage: undefined };
    }
    default:
    case 'resetHoverData':
      return {
        ...state,
        hoverImage: undefined,
        displayName: undefined,
        description: undefined
      };
  }
};

export const ImageAttributeProvider = ({ children }: { children: React.ReactChild }) => {
  const [state, dispatch] = useReducer(ownReducer, {});

  const hover = useCallback(
    (args: AttributeHoverPayload) => {
      dispatch({ type: 'setHoverData', ...args });
    },
    [dispatch]
  );
  const setProductImage = useCallback(
    (args: AttributeProductImagePayload) => {
      dispatch({ type: 'setProductImage', ...args });
    },
    [dispatch]
  );
  const resetHover = useCallback(() => {
    dispatch({ type: 'resetHoverData' });
  }, [dispatch]);
  const resetProductImage = useCallback(() => {
    dispatch({ type: 'resetProductImage' });
  }, [dispatch]);
  const ctxValue: ImageAttributeContextState = {
    ...state,
    hover,
    resetHover,
    setProductImage,
    resetProductImage
  };
  return (
    <ImageAttributeContext.Provider value={ctxValue}>{children}</ImageAttributeContext.Provider>
  );
};
